import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../components/layout';
import { Grid, Plus, LayoutGrid, List, Clock, Trash2, Eye, Bell, Edit3 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { BrandDropdown } from '../components/common/BrandDropdown';
import { DateRangePicker } from '../components/common/DateRangeSelector';
import { format, isAfter, parseISO, isValid, subMonths } from 'date-fns';
import { SocialMediaCard } from '../components/calendar/SocialMediaCard';
import { PostStatusDropdown, type PostStatus } from '../components/common/PostStatusDropdown';
import { BoardView } from '../components/calendar/BoardView';
import { CreatePostModal } from '../components/posts/CreatePostModal';
import { PostForm } from '../components/posts/PostForm';
import { SocialIcon } from '../components/icons/SocialIcon';
import { DeleteConfirmationModal } from '../components/common/DeleteConfirmationModal';
import { StatusModal } from '../components/common/StatusModal';
import { ActionsMenu } from '../components/common/ActionsMenu';
import { NoPostsCard } from '../components/common/NoPostsCard';
import { PostPreview } from '../components/calendar/PostPreview';
import { ErrorHandlingMedia } from '../components/common/ErrorHandlingMedia';
import { auth } from '../config/firebase';
import { checkPostStatus } from '../services/postService';
import { useContentHubUrlState } from '../store/contentHubStore';
import type { Brand } from '../types';
import type { Post } from '../types/calendar';
import { usePosts } from '../hooks/usePosts';
import { useDeletePost } from '../hooks/useDeletePost';
import { useBulkUpdatePosts } from '../hooks/useBulkUpdatePosts';
import { BulkEditModal, type BulkUpdateData } from '../components/modals/BulkEditModal';
import { formatLocaleDate } from '../utils/dateUtils';
import { logger } from '../utils/logger';
import { normalizeMedia } from '../utils/mediaUtils';
import { MediaStructureDebug } from '../components/debug/MediaStructureDebug';

// Utility functions copied from Calendar.tsx
const truncateContent = (content: string, maxLength: number = 70) => {
  if (content.length <= maxLength) return content;
  return content.substring(0, maxLength - 3) + '...';
};

// Helper function to capitalize the first letter of a string
const capitalize = (s: string) => {
  if (typeof s !== 'string' || s.length === 0) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// Enhanced status display helper
const getStatusDisplayInfo = (post: Post, currentUserId: string | undefined, t: Function) => {
  const status = post.status as PostStatus; // Assert type

  if (status === 'waiting_for_approval') {
    if (currentUserId && currentUserId === post.reviewerId) {
      // Waiting for CURRENT user's review
      return { 
        name: t('status.needsYourApproval', 'Needs Your Review'), 
        emoji: '🔔', // Bell emoji
        colorClasses: 'bg-orange-200 text-orange-800 border border-orange-400' // Brighter/distinct orange
      };
    } else if (currentUserId && currentUserId === post.userId) {
       // Waiting for SOMEONE ELSE's review (current user is author)
       return { 
        name: t('status.waiting_for_approval', 'Waiting Approval'), 
        emoji: '⏳', 
        colorClasses: 'bg-orange-100 text-orange-700' // Standard orange
      };
    } else {
      // Fallback if IDs don't match (shouldn't happen often)
       return { 
        name: t('status.waiting_for_approval', 'Waiting Approval'), 
        emoji: '⏳', 
        colorClasses: 'bg-orange-100 text-orange-700' 
      };
    }
  }

  // Handle other statuses
  switch (status) {
    case 'draft': 
      return { name: t('status.draft', 'Draft'), emoji: '📝', colorClasses: 'bg-gray-100 text-gray-700' };
    case 'scheduled': 
      return { name: t('status.scheduled', 'Scheduled'), emoji: '📅', colorClasses: 'bg-blue-100 text-blue-700' };
    case 'ready_to_publish':
      return { name: t('status.ready_to_publish', 'Ready to Publish'), emoji: '🚀', colorClasses: 'bg-teal-100 text-teal-700' };
    case 'processing': 
      return { name: t('status.processing', 'Processing'), emoji: '⚙️', colorClasses: 'bg-purple-100 text-purple-700' };
    case 'published': 
      return { name: t('status.published', 'Published'), emoji: '✅', colorClasses: 'bg-green-100 text-green-700' };
    case 'rejected': 
      return { name: t('status.rejected', 'Rejected'), emoji: '❌', colorClasses: 'bg-red-100 text-red-700' };
    case 'error': 
       return { name: t('status.error', 'Error'), emoji: '⚠️', colorClasses: 'bg-red-100 text-red-700' };
     // Remove pending/waiting if not used by API
    // case 'pending': 
    //   return { name: t('status.pending', 'Pending'), emoji: '🚧', colorClasses: 'bg-yellow-100 text-yellow-700' };
    // case 'waiting': 
    //   return { name: t('status.waiting', 'Waiting'), emoji: '⏳', colorClasses: 'bg-orange-100 text-orange-700' };
    default:
      // Fallback for unknown statuses
      return { name: capitalize(status), emoji: '❓', colorClasses: 'bg-gray-100 text-gray-700' };
  }
};

const MyContentHub = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    dateRange, setDateRange,
    customDateRange, setCustomDateRange,
    selectedBrand, setSelectedBrand,
    view, setView,
    statusFilter, setStatusFilter
  } = useContentHubUrlState();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isPostFormOpen, setIsPostFormOpen] = useState(false);
  const [selectedPostForEdit, setSelectedPostForEdit] = useState<Partial<Post> | null>(null);
  const [selectedPosts, setSelectedPosts] = useState<string[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusCheckLoading, setStatusCheckLoading] = useState<string | null>(null);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  
  // States for hover preview functionality
  const [hoveredPost, setHoveredPost] = useState<string | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  // State for media debug mode
  const [debugMode, setDebugMode] = useState(false);

  const { data: postsData, isLoading: isLoadingPosts, error: postsError } = usePosts();
  const posts: Post[] = (postsData?.posts as Post[]) || [];
  const deletePostMutation = useDeletePost();
  const bulkUpdateMutation = useBulkUpdatePosts();

  const statuses = useMemo(() => [
    { id: 'all', label: t('statusDropdown.all', 'All Statuses'), emoji: '🌐', description: t('statusDropdown.allDesc', 'View posts across all their current states.') },
    { id: 'draft', label: t('statusDropdown.draft', 'Draft'), emoji: '📝', description: t('statusDropdown.draftDesc', 'Post is in progress, not yet submitted for review or scheduling.') },
    { id: 'scheduled', label: t('statusDropdown.scheduled', 'Scheduled'), emoji: '📆', description: t('statusDropdown.scheduledDesc', 'Post is approved and set for automatic publishing at a future time.') },
    { id: 'ready_to_publish', label: t('statusDropdown.ready_to_publish', 'Ready to Publish'), emoji: '🚀', description: t('statusDropdown.ready_to_publishDesc', 'Post is complete, approved, and awaiting its scheduled time or manual publishing.') },
    { id: 'waiting_for_approval', label: t('statusDropdown.waiting_for_approval', 'Waiting for approval'), emoji: '⏳', description: t('statusDropdown.waiting_for_approvalDesc', 'Post has been submitted and is awaiting review by an approver.') },
    { id: 'published', label: t('statusDropdown.published', 'Published'), emoji: '✅', description: t('statusDropdown.publishedDesc', 'Post has been successfully shared on the selected platforms.') },
    { id: 'rejected', label: t('statusDropdown.rejected', 'Rejected'), emoji: '❌', description: t('statusDropdown.rejectedDesc', 'Post was reviewed but not approved for publishing by an approver.') },
    { id: 'error', label: t('statusDropdown.error', 'Error'), emoji: '⚠️', description: t('statusDropdown.errorDesc', 'An error occurred while trying to publish or process the post.') },
    { id: 'processing', label: t('statusDropdown.processing', 'Processing'), emoji: '⚙️', description: t('statusDropdown.processingDesc', 'Post is currently being processed by the system (e.g., media upload, API submission).') },
    { id: 'pending', label: t('statusDropdown.pending', 'Pending'), emoji: '🚧', description: t('statusDropdown.pendingDesc', 'Post is awaiting system action or external confirmation before proceeding.') },
  ], [t, i18n.language]);

  useEffect(() => {
    document.title = t('contentHub.title', 'My Content Hub');
  }, [t]);

  useEffect(() => {
    if (view !== 'list' && view !== 'grid' && view !== 'board') {
      setView('list');
    }
  }, [view]);

  useEffect(() => {
    const handleFocus = () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [queryClient]);

  if (isLoadingPosts) {
    // Skeleton Loader for Content Hub Page
    return (
      <div className="min-h-screen bg-white lg:ml-16 pt-4 lg:pt-0 animate-pulse">
        <div className="w-[95%] mx-auto py-8">
          {/* Header Skeleton */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div className="flex-1 min-w-0">
              <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
            <div className="h-10 bg-gray-200 rounded-lg w-32"></div>
          </div>
          {/* View Toggles & Filters Skeleton */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-2">
              <div className="h-8 w-16 bg-gray-200 rounded-lg"></div>
              <div className="h-8 w-16 bg-gray-200 rounded-lg"></div>
              <div className="h-8 w-16 bg-gray-200 rounded-lg"></div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
              <div className="h-10 bg-gray-200 rounded-lg w-full sm:w-40"></div>
              <div className="h-10 bg-gray-200 rounded-lg w-full sm:w-40"></div>
              <div className="h-10 bg-gray-200 rounded-lg w-full sm:w-40"></div>
            </div>
          </div>
          {/* Content Area Skeleton (mimics list view) */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {[1, 2, 3, 4, 5, 6, 7].map(i => (
                      <th key={i} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <tr key={i}>
                      <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 w-4 bg-gray-200 rounded"></div></td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 bg-gray-200 rounded mr-4"></div>
                          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                         <div className="flex gap-1">
                            <div className="h-4 w-4 bg-gray-200 rounded-full"></div>
                            <div className="h-4 w-4 bg-gray-200 rounded-full"></div>
                          </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell"><div className="h-6 w-24 bg-gray-200 rounded"></div></td>
                      <td className="px-6 py-4 whitespace-nowrap"><div className="h-5 w-16 bg-gray-200 rounded-full"></div></td>
                      <td className="px-6 py-4 whitespace-nowrap hidden lg:table-cell"><div className="h-4 w-20 bg-gray-200 rounded"></div></td>
                      <td className="px-6 py-4 whitespace-nowrap text-right"><div className="h-5 w-5 bg-gray-200 rounded"></div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (postsError) {
    return (
      <div className="min-h-screen bg-white lg:ml-16 pt-4 lg:pt-0">
        <div className="w-[95%] mx-auto py-8 flex items-center justify-center h-96">
          <p className="text-red-600">Failed to load posts. Please try again later.</p>
        </div>
      </div>
    );
  }

  const brands: Brand[] = [
    { id: 'all', name: t('brandDropdown.allBrands', 'All Brands'), logo: '/logo.svg' },
    ...Array.from(
      new Map(posts.map((post: any) => [post.brandId, { 
        id: post.brandId || 'unknown', 
        name: post.brandName || 'Unknown Brand', 
        logo: post.brandLogo || '/logo.svg'
      }])).values()
    ) as Brand[]
  ];

  const filteredPosts = posts.filter((post: any) => {
    if (selectedBrand !== 'all' && post.brandId !== selectedBrand) return false;
    if (statusFilter !== 'all' && post.status !== statusFilter) return false;
    return true;
  });

  const sortedPosts = [...filteredPosts].sort((a, b) => {
    let dateA, dateB;
    try {
      const schedA = (a as any).scheduledFor;
      dateA = schedA ? parseISO(schedA) : a.updatedAt ? parseISO(a.updatedAt) : new Date(0);
    } catch { dateA = new Date(0); }
    try {
      const schedB = (b as any).scheduledFor;
      dateB = schedB ? parseISO(schedB) : b.updatedAt ? parseISO(b.updatedAt) : new Date(0);
    } catch { dateB = new Date(0); }
    if (!isValid(dateA)) dateA = new Date(0);
    if (!isValid(dateB)) dateB = new Date(0);
    return dateB.getTime() - dateA.getTime();
  });

  const now = new Date();
  const nextScheduledPost = sortedPosts.find((post: any) => {
    const sched = (post as any).scheduledFor;
    return sched && typeof sched === 'string' && sched.trim() && isValid(parseISO(sched)) && isAfter(parseISO(sched), now);
  });

  const handlePostClick = (post: Post) => {
    navigate(`/posts/${post.id}/edit`);
  };

  const handleSelectPost = (postId: string, e: React.MouseEvent | React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    setSelectedPosts((prev) => prev.includes(postId) ? prev.filter((id) => id !== postId) : [...prev, postId]);
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedPosts(e.target.checked ? filteredPosts.map(post => post.id) : []);
  };

  const handleBulkDelete = async () => {
    if (selectedPosts.length === 0) return;

    try {
      const deletePromises = selectedPosts.map(postId => deletePostMutation.mutateAsync(postId));
      await Promise.all(deletePromises);
      logger.info('MyContentHub', `Successfully deleted ${selectedPosts.length} posts`);
      setSelectedPosts([]);
    } catch (error) {
      logger.error('MyContentHub', 'Error deleting posts', error);
      alert('Failed to delete posts. Please try again.');
    }
  };

  const handleBulkEdit = async (updateData: BulkUpdateData) => {
    console.log('📨 MyContentHub: handleBulkEdit called - API calls will now start');
    console.log('📊 Update data received:', updateData);
    console.log('📝 Posts to update:', selectedPosts);
    
    if (selectedPosts.length === 0) return;

    try {
      const result = await bulkUpdateMutation.mutateAsync({
        postIds: selectedPosts,
        updateData: {
          status: updateData.status,
          brandId: updateData.brandId,
          platforms: updateData.platforms as string[]
        }
      });

      logger.info('MyContentHub', `Bulk edit completed: ${result.updated} updated, ${result.failed.length} failed`);
      
      if (result.success) {
        setSelectedPosts([]);
        setShowBulkEditModal(false);
        
        if (result.failed.length > 0) {
          alert(`${result.updated} posts updated successfully. ${result.failed.length} posts failed to update.`);
        } else {
          alert(`All ${result.updated} posts updated successfully!`);
        }
      } else {
        alert(`Failed to update posts. ${result.errors.length} errors occurred.`);
      }
    } catch (error) {
      logger.error('MyContentHub', 'Error in bulk edit', error);
      alert('Failed to update posts. Please try again.');
    }
  };

  const handleDeletePost = (postId: string) => {
    setSelectedPosts([postId]);
    setShowDeleteModal(true);
    setHoveredPost(null); // Hide preview when modal opens
  };

  const handleDuplicatePost = (post: Post) => {
    const contentCopy = `[COPY] ${post.content || ''}`;
    const normalizedMedia = normalizeMedia(post.media);
    const firstPlatform = post.platforms?.[0];
    navigate('/posts/new', {
      state: {
        content: contentCopy,
        platform: firstPlatform,
        brandId: (post as any).brandId || (post as any).brand_id || undefined,
        imageUrl: normalizedMedia ? normalizedMedia.url : null
      }
    });
  };

  const handleCheckStatus = async (postId: string) => {
    try {
      setStatusMessage('Checking status...');
      setStatusModalOpen(true);
    } catch (error) {
      logger.error('MyContentHub', 'Failed to check post status', error);
      setStatusMessage('Error checking status');
      setStatusModalOpen(true);
    }
  };

  // Hover handlers for preview
  const handleRowMouseEnter = (e: React.MouseEvent, postId: string) => {
    setHoveredPost(postId);
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  const handleRowMouseMove = (e: React.MouseEvent) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  const handleRowMouseLeave = () => {
    setHoveredPost(null);
  };

  const getOptimalPosition = () => {
    // Dynamically size the preview card so it never exceeds 90% of the viewport on mobile
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const cardWidth = Math.min(800, viewportWidth * 0.9);
    const cardHeight = Math.min(500, viewportHeight * 0.8);
    const margin = 20; // Margin from screen edges

    let x = mousePosition.x + 10; // Default: to the right of cursor
    let y = mousePosition.y - 100; // Default: above cursor

    // Prevent right overflow
    if (x + cardWidth + margin > viewportWidth) {
      x = mousePosition.x - cardWidth - 10;
    }

    // Prevent left overflow
    if (x < margin) {
      x = margin;
    }

    // Prevent bottom overflow
    if (y + cardHeight + margin > viewportHeight) {
      y = viewportHeight - cardHeight - margin;
    }

    // Prevent top overflow
    if (y < margin) {
      y = margin;
    }

    return { x, y };
  };

  const handleStatusModalClose = () => {
    setStatusModalOpen(false);
  };

  const getDeleteConfirmationMessage = () => {
    if (selectedPosts.length === 1) {
      const post = posts.find((p: Post) => p.id === selectedPosts[0]);
      return `Are you sure you want to delete the post "${truncateContent(post?.content || '', 30)}"? This action cannot be undone.`;
    }
    return `Are you sure you want to delete ${selectedPosts.length} selected posts? This action cannot be undone.`;
  };

  const handleCreateWithAI = () => {
    logger.info('MyContentHub', 'Creating post with AI');
    setIsCreateModalOpen(false);
    navigate('/content-creation');
  };

  const handleCreateManually = () => {
    setIsCreateModalOpen(false);
    navigate('/posts/new');
  };

  const handlePostSubmit = async (data: Partial<Post> & { publishNow?: boolean }) => {
    logger.info('MyContentHub', 'Post submitted in Content Hub (needs implementation)');
    setIsPostFormOpen(false);
    setSelectedPostForEdit(null);
  };

  return (
    <>
      <PageLayout
        title={t('contentHub.title', 'My Content Hub')}
        subtitle={t('contentHub.subtitle', 'Manage all your content in one place')}
        actions={
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setDebugMode(!debugMode)}
              className={`inline-flex items-center px-3 py-2 rounded-lg transition-colors text-sm ${debugMode ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              title="Toggle Media Debug Mode"
            >
              🔧 {debugMode ? 'Debug ON' : 'Debug'}
            </button>
            {selectedPosts.length > 0 && (
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => setShowBulkEditModal(true)}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Edit3 className="w-5 h-5 mr-2" />
                  Edit {selectedPosts.length} {selectedPosts.length === 1 ? 'Post' : 'Posts'}
                </button>
                <button 
                  onClick={() => {
                    setShowDeleteModal(true);
                    setHoveredPost(null); // Hide preview when modal opens
                  }}
                  className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Trash2 className="w-5 h-5 mr-2" />
                  Delete {selectedPosts.length} {selectedPosts.length === 1 ? 'Post' : 'Posts'}
                </button>
              </div>
            )}
            <button 
              onClick={() => setIsCreateModalOpen(true)}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('sidebar.createContent')}
            </button>
          </div>
        }
      >

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setView('list')}
              className={`inline-flex items-center px-3 py-1.5 rounded-lg ${view === 'list' ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <List className="w-4 h-4 mr-2" />
              {t('contentHub.view.list', 'List')}
            </button>
            <button
              onClick={() => setView('grid')}
              className={`inline-flex items-center px-3 py-1.5 rounded-lg ${view === 'grid' ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <Grid className="w-4 h-4 mr-2" />
              {t('contentHub.view.grid', 'Grid')}
            </button>
            <button
              onClick={() => setView('board')}
              className={`inline-flex items-center px-3 py-1.5 rounded-lg ${view === 'board' ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <LayoutGrid className="w-4 h-4 mr-2" />
              {t('contentHub.view.board', 'Board')}
            </button>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
            <DateRangePicker
              dateRange={dateRange}
              customDateRange={customDateRange}
              onDateRangeChange={setDateRange}
              onCustomDateRangeChange={setCustomDateRange}
            />
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <PostStatusDropdown 
                value={statusFilter} 
                onChange={(status) => {
                  console.log('🔍 FILTER DROPDOWN: Status filter changed to', status);
                  setStatusFilter(status);
                }} 
              />
              <BrandDropdown brands={brands} selectedBrandId={selectedBrand} onBrandSelect={setSelectedBrand} />
            </div>
          </div>
        </div>

        {view === 'list' ? (
            <div className="bg-white rounded-xl border border-black shadow-lg overflow-hidden">
              {selectedPosts.length > 0 && (
                <div className="px-6 py-3 bg-primary-50 border-b border-primary-100 flex items-center justify-between">
                   <div className="flex items-center gap-2">
                    <span className="text-primary-700 font-medium">
                      {selectedPosts.length} {selectedPosts.length === 1 ? 'post' : 'posts'} selected
                    </span>
                    <button onClick={() => setSelectedPosts([])} className="text-primary-600 text-sm hover:text-primary-800 ml-2">
                      Clear selection
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setShowBulkEditModal(true)} className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
                      <Edit3 className="w-4 h-4 mr-1" /> Edit {selectedPosts.length} posts
                    </button>
                    <button onClick={() => setShowDeleteModal(true)} className="inline-flex items-center px-3 py-1 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700">
                      <Trash2 className="w-4 h-4 mr-1" /> Delete selected
                    </button>
                  </div>
                </div>
              )}
              
              {nextScheduledPost && (
                  <div className="px-6 py-3 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-blue-700">
                      Next scheduled post: {formatLocaleDate((nextScheduledPost as any)?.scheduledFor, i18n.language)}
                    </span>
                  </div>
               )}
              {filteredPosts.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                         <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                            <input type="checkbox" checked={selectedPosts.length > 0 && selectedPosts.length === filteredPosts.length} onChange={handleSelectAll} className="h-4 w-4 text-primary-600 border-gray-300 rounded cursor-pointer" title="Select all posts" />
                          </th>
                          <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Content</th>
                          <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Platforms</th>
                          <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Brand</th>
                          <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Date</th>
                          <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                     <tbody className="bg-white divide-y divide-gray-200">
                        {sortedPosts.map((post: any) => {
                          const isNext = nextScheduledPost?.id === post.id;
                          const isCheckingStatus = statusCheckLoading === post.id;
                          const currentUserId = auth.currentUser?.uid;
                          const { name: statusName, emoji: statusEmoji, colorClasses: statusColorClasses } = getStatusDisplayInfo(post, currentUserId, t);
                          const platformSettings = (post as any).platformSettings;
                          const hasProcessingStatus =
                            post.status === 'processing' ||
                            (platformSettings && Object.values(platformSettings).some((settings: any) => settings?.status === 'processing'));
                          return (
                            <tr 
                              key={post.id}
                              onClick={() => handlePostClick(post)}
                              className={`hover:bg-gray-50 cursor-pointer ${isNext ? 'bg-blue-50' : ''} ${selectedPosts.includes(post.id) ? 'bg-primary-50' : ''}`}
                              onMouseEnter={(e) => handleRowMouseEnter(e, post.id)}
                              onMouseMove={(e) => handleRowMouseMove(e)}
                              onMouseLeave={handleRowMouseLeave}
                            >
                               <td className="px-3 sm:px-6 py-2 sm:py-4" onClick={(e) => e.stopPropagation()}>
                                <input type="checkbox" checked={selectedPosts.includes(post.id)} onChange={(e) => handleSelectPost(post.id, e)} className="h-4 w-4 text-primary-600 border-gray-300 rounded cursor-pointer" />
                              </td>
                              <td className="px-3 sm:px-6 py-2 sm:py-4">
                                <div className="flex items-start gap-3">
                                  {(() => {
                                    const normalizedMedia = normalizeMedia(post.media);
                                    return normalizedMedia && (
                                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded bg-gray-100 flex-shrink-0 overflow-hidden">
                                        <ErrorHandlingMedia
                                          src={normalizedMedia.url}
                                          alt="Post media"
                                          type={normalizedMedia.type}
                                          className="w-full h-full object-cover"
                                          controls={false}
                                        />
                                      </div>
                                    );
                                  })()}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs sm:text-sm text-gray-900">{truncateContent(post.content)}</p>
                                    <p className="text-xs text-gray-500 hidden sm:block">{post.format === 'reel' ? 'Reel' : 'Regular post'}</p>
                                    
                                    <div className="flex items-center gap-1 mt-1 sm:hidden">
                                      {post.platforms.map((platform: string) => <SocialIcon key={platform} platform={platform as any} size={12}/>)}
                                    </div>
                                    
                                    {/* Debug Mode - Show Media Structure */}
                                    {debugMode && (
                                      <div className="mt-2">
                                        <MediaStructureDebug 
                                          media={post.media} 
                                          postId={post.id}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="px-3 sm:px-6 py-2 sm:py-4 hidden sm:table-cell">
                                <div className="flex gap-1">
                                  {post.platforms.map((platform: string) => <SocialIcon key={platform} platform={platform as any} size={16}/>)}
                                </div>
                              </td>
                              
                              <td className="px-3 sm:px-6 py-2 sm:py-4 hidden md:table-cell">
                                {post.brandLogo && post.brandName ? (
                                  <div className="flex items-center">
                                    <img src={post.brandLogo} alt={post.brandName} className="w-8 h-6 rounded-lg object-contain mr-2"/>
                                    <span className="text-sm text-gray-900">{post.brandName}</span>
                                  </div>
                                ) : <span className="text-sm text-gray-500">No brand</span>}
                              </td>
                              
                              <td className="px-3 sm:px-6 py-2 sm:py-4">
                                <div className="relative group inline-block">
                                  {post.platforms.length > 1 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {post.platforms.map((platform: string) => {
                                        const platformStatus = (post as any).platformSettings?.[platform]?.status || post.status;
                                        const { name: statusName, emoji: statusEmoji, colorClasses: statusColorClasses } = getStatusDisplayInfo({ ...post, status: platformStatus }, auth.currentUser?.uid, t);
                                        return (
                                          <span key={platform} className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColorClasses}`}
                                            title={statuses.find(s => s.id === platformStatus)?.description || ''}
                                          >
                                            <SocialIcon platform={platform as any} size={14} /> {statusEmoji} {statusName}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColorClasses}`}>
                                      {statusEmoji} {statusName}
                                    </span>
                                  )}
                                  <div className="absolute left-1/2 z-20 -translate-x-1/2 mt-2 w-56 bg-black text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 shadow-lg whitespace-normal">
                                    {post.platforms.length > 1
                                      ? post.platforms.map((platform: string) => {
                                          const platformStatus = (post as any).platformSettings?.[platform]?.status || post.status;
                                          return (
                                            <div key={platform} className="flex items-center gap-1 mb-1 last:mb-0">
                                              <SocialIcon platform={platform as any} size={12} />
                                              <span>{statuses.find(s => s.id === platformStatus)?.description || ''}</span>
                                            </div>
                                          );
                                        })
                                      : (statuses.find(s => s.id === post.status)?.description || '')}
                                  </div>
                                </div>
                              </td>
                              
                              <td className="px-3 sm:px-6 py-2 sm:py-4 text-sm text-gray-500 hidden lg:table-cell">
                                {formatLocaleDate(((post as any).scheduledFor) || post.updatedAt, i18n.language)}
                              </td>
                              
                              <td className="px-3 sm:px-6 py-2 sm:py-4 text-right">
                                {isCheckingStatus ? (
                                  <div className="flex justify-end"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div></div>
                                ) : (
                                  <ActionsMenu 
                                    onModify={() => handlePostClick(post)}
                                    onDelete={() => handleDeletePost(post.id)}
                                    onDuplicate={() => handleDuplicatePost(post)}
                                    onCheckStatus={hasProcessingStatus ? () => handleCheckStatus(post.id) : undefined}
                                    status={post.status}
                                    menuPosition="top"
                                    isAnyProcessing={hasProcessingStatus}
                                  />
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                  </table>
                </div>
              ) : (
                 <NoPostsCard 
                    title="No posts found"
                    message="You haven't created any posts yet. Get started by creating your first post!"
                    icon="list"
                    isFiltered={selectedBrand !== 'all' || statusFilter !== 'all'}
                  />
              )}
            </div>
          ) : view === 'grid' ? (
            <>
             {filteredPosts.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {filteredPosts.map((post: any) => (
                    <div 
                      key={post.id} 
                      onClick={() => handlePostClick(post)}
                      className="cursor-pointer transform transition-transform hover:scale-[1.02]"
                    >
                      <SocialMediaCard 
                        post={post}
                        brandLogo={post.brandLogo}
                        username={post.brandName ? post.brandName.toLowerCase().replace(/\s/g, '') : ''}
                        brandName={post.brandName}
                        onDelete={handleDeletePost}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <NoPostsCard 
                  title="Your content gallery is empty"
                  message="Time to showcase your creativity! Create your first post and watch your social media presence grow."
                  icon="image"
                  isFiltered={selectedBrand !== 'all' || statusFilter !== 'all'}
                />
              )}
            </>
          ) : view === 'board' ? (
            <BoardView posts={filteredPosts} onPostClick={handlePostClick} />
          ) : (
             <p>Unknown view selected</p>
          )}
      </PageLayout>

      <CreatePostModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreateWithAI={handleCreateWithAI}
        onCreateManually={handleCreateManually}
      />
      <PostForm
        isOpen={isPostFormOpen}
        onClose={() => { setIsPostFormOpen(false); setSelectedPostForEdit(null); }}
        onSubmit={handlePostSubmit}
        initialData={selectedPostForEdit}
      />
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleBulkDelete}
        title={`Delete ${selectedPosts.length === 1 ? 'Post' : 'Selected Posts'}`}
        message={getDeleteConfirmationMessage()}
        warning="All associated data and media files will be permanently deleted."
      />
      <StatusModal
        isOpen={statusModalOpen}
        onClose={handleStatusModalClose}
        status={statusMessage}
        autoCloseDelay={4000}
      />
      <BulkEditModal
        isOpen={showBulkEditModal}
        onClose={() => setShowBulkEditModal(false)}
        onConfirm={handleBulkEdit}
        selectedPostsCount={selectedPosts.length}
        brands={brands}
        isLoading={bulkUpdateMutation.isPending}
      />

      {/* Hover Preview Tooltip for List View */}
      {view === 'list' && hoveredPost && !showDeleteModal && !showBulkEditModal && !statusModalOpen && (
        <div 
          className="fixed pointer-events-none z-50"
          style={{
            left: getOptimalPosition().x,
            top: getOptimalPosition().y,
          }}
        >
          <PostPreview post={sortedPosts.find((p: any) => p.id === hoveredPost)!} />
        </div>
      )}
    </>
  );
};

export default MyContentHub; 